import React, { useState, useEffect, useCallback } from 'react';
import { Offcanvas, Form, Table, Button, Spinner, OverlayTrigger, Tooltip, Row, Col } from 'react-bootstrap'; // 🔥 Added OverlayTrigger and Tooltip
import toast from 'react-hot-toast';
import { getAvailableUsersList, assignUserToDepartment } from './departmentHelper';
import { truncateText } from '../../utils';
import { extractErrorMessage } from '../../constants/common';
import { PER_PAGE } from '../../constants/user';
import PaginationComponent from '../common/Pagination';

const AddEmployeeOffcanvas = ({
    show,
    onHide,
    searchTerm,
    setSearchTerm,
    departmentId,
    departmentName,
    onEmployeeAdded
}) => {
    const [availableEmployees, setAvailableEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(null); // Track which employee is being assigned
    const [current_page, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    const handlePagination = (page) => {
        setCurrentPage(page);
    };
    // Function to fetch available employees
    const fetchAvailableEmployees = async (query = '') => {
        if (!departmentId) return;

        setLoading(true);
        try {
            const response = await getAvailableUsersList(departmentId, query, current_page,PER_PAGE);
            if (response.status === 'success') {
                const transformedEmployees = response.data.lists.map(user => ({
                    id: user.user_id,
                    name: `${user.name}${user.last_name ? ' ' + user.last_name : ''}`,
                    email: user.email,
                    user_id: user.user_id
                }));
                setTotalCount(response.data.total_count);
                setAvailableEmployees(transformedEmployees);
            }
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Debounced version of fetch function
    const debouncedFetch = useCallback(
        debounce((query) => fetchAvailableEmployees(query), 300),
        [departmentId]
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Call API when search term has 3 or more characters, or when it's empty
        if (value.length >= 3 || value.length === 0) {
            debouncedFetch(value);
        }
    };

    // Handle adding employee to department
    const handleAddEmployee = async (userId) => {
        setAssigning(userId);
        try {
            const response = await assignUserToDepartment(departmentId, userId);
            if (response.status === 'success') {
                toast.success(response.message || 'User assigned to department successfully');
                setSearchTerm('');
                await fetchAvailableEmployees('');
                if (onEmployeeAdded) {
                    onEmployeeAdded();
                }
            }
        } catch (error) {
            console.error('Error assigning employee:', error);
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setAssigning(null);
        }
    };

    // Fetch available employees when component mounts or when show changes
    useEffect(() => {
        if (show && departmentId) {
            fetchAvailableEmployees(searchTerm);
        }
    }, [current_page,show, departmentId]);

    // Filter employees based on search term for display
    const filteredEmployees = availableEmployees.filter((emp) =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title className="!text-xl">
                    Add employee to {truncateText(departmentName, 20, "bottom") || 'Department'}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Form.Group className="mb-4">
                    <Form.Control
                        type="text"
                        placeholder="Search Employee (minimum 3 characters)"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </Form.Group>

                {loading ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Loading employees...</span>
                    </div>
                ) : (
                    <div className="table-responsive" style={{ overflowY: 'auto' }}>
                        <Table responsive className="core-ui-table" size="sm">
                            <thead className="sticky-top bg-white">
                                <tr>
                                    <th className="text-left">Employee Name</th>
                                    <th className="text-left">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.length > 0 ? (
                                    filteredEmployees.map((emp) => (
                                        <tr key={emp.id}>
                                            {/* 🔥 Employee name with truncation and tooltip */}
                                            <td className="text-left w-100">
                                                <div>
                                                    <div className="fw-semibold">
                                                        {truncateText(emp.name, 25)}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {truncateText(emp.email, 30)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="no-hover"
                                                    onClick={() => handleAddEmployee(emp.user_id)}
                                                    disabled={assigning === emp.user_id}
                                                    title={`Add ${emp.name} to department`} // 🔥 Added tooltip for action button
                                                >
                                                    {assigning === emp.user_id ? (
                                                        <>
                                                            <Spinner
                                                                animation="border"
                                                                size="sm"
                                                                className="me-1"
                                                            />
                                                            Adding...
                                                        </>
                                                    ) : (
                                                        'Add'
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="text-center text-muted py-4"> {/* 🔥 Updated colSpan from 3 to 2 and improved styling */}
                                            <div>
                                                <p className="mb-0">
                                                    {searchTerm.length > 0 && searchTerm.length < 3
                                                        ? 'Type at least 3 characters to search'
                                                        : 'No employees found.'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
                {totalCount > PER_PAGE && (
                    <Row className="mt-4">
                        <Col md={12} className="d-flex justify-content-center">
                            <PaginationComponent
                                total_count={totalCount}
                                current_page={current_page}
                                handleOnClick={handlePagination}
                                per_page={PER_PAGE}
                            />
                        </Col>
                    </Row>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AddEmployeeOffcanvas;